<template>
    <div
        ref="card"
        :style="
            isDesktop
                ? `transform-style: preserve-3d; transform: perspective(1000px)`
                : ''
        "
        :class="cardClasses"
        class="group relative h-full overflow-hidden rounded-12 bg-black/5 from-white/10 to-accent/30 p-3 shadow-card transition-all duration-300 ease-out md:hover:shadow-card-hover">
        <div
            ref="glow"
            style="
                background-image: radial-gradient(
                    circle at 50% -20%,
                    #ffffff22,
                    #0000000f
                );
            "
            class="absolute inset-0 size-full opacity-0 transition-opacity duration-300" />
        <div
            :class="focusClasses"
            class="absolute aspect-square w-1/2 -rotate-12 scale-100 rounded-full bg-accent/50 blur-[5rem] transition-all delay-300 duration-1000 ease-in-out max-md:hidden md:group-hover:scale-0 md:group-hover:opacity-0" />
        <div class="relative z-40 size-full rounded-20">
            <slot />
        </div>
    </div>
</template>

<script setup lang="ts">
import type { TGradientType } from '~/types/global.types'

const props = defineProps({
    gradient: {
        type: String as PropType<TGradientType>,
        default: 'top-left',
    },
})

let cardClasses = ''
let focusClasses = ''
const isDesktop = ref(false)

switch (props.gradient) {
    case 'top-left': {
        cardClasses = 'bg-gradient-to-tl'
        focusClasses = '-left-10 -top-12'
        break
    }
    case 'top-right': {
        cardClasses = 'bg-gradient-to-tr'
        focusClasses = '-right-10 -top-12'
        break
    }
    case 'bottom': {
        cardClasses = 'bg-gradient-to-b'
        focusClasses = '-bottom-12 left-1/2 -translate-x-1/2'
        break
    }
    case 'bottom-right': {
        cardClasses = 'bg-gradient-to-br'
        focusClasses = '-bottom-12 -right-10'
        break
    }
    case 'left': {
        cardClasses = 'bg-gradient-to-l'
        focusClasses = '-left-14 top-1/2 -translate-y-1/2'
        break
    }
}

const card = ref<HTMLElement | null>(null)
const glow = ref<HTMLElement | null>(null)

// Отступ от краёв карточки для запуска анимации
const OFFSET = 35

function handleMouseMove(event: MouseEvent): void {
    if (!card.value || !glow.value || !isDesktop.value) return

    const rect: DOMRect = card.value.getBoundingClientRect()
    const x: number = event.clientX - rect.left
    const y: number = event.clientY - rect.top

    const mouseNotInArea =
        x < OFFSET ||
        y < OFFSET ||
        x > rect.width - OFFSET ||
        y > rect.height - OFFSET

    if (mouseNotInArea) {
        handleMouseLeave()
        return
    }

    const centerX = rect.width / 2
    const centerY = rect.height / 2

    const percentX = (x - centerX) / centerX
    const percentY = -((y - centerY) / centerY)

    card.value.style.transform = `perspective(1000px) rotateY(${percentX * 10}deg) rotateX(${percentY * 10}deg) scale(1.07)`
    glow.value.style.opacity = '1'
    glow.value.style.backgroundImage = `
    radial-gradient(
      circle at ${x}px ${y}px,
      #743FDA80,
      #0000000f
    )
  `
}

function handleMouseLeave() {
    if (!card.value || !glow.value || !isDesktop.value) return

    card.value.style.transform = `perspective(1000px) rotateY(0deg) rotateX(0deg)`
    glow.value.style.opacity = '0'
}

function checkIsDesktop() {
    if (!window) return
    isDesktop.value = window.innerWidth >= 1024
}

onMounted(() => {
    if (!card.value) return

    checkIsDesktop()
    card.value.addEventListener('mousemove', handleMouseMove)
    card.value.addEventListener('mouseleave', handleMouseLeave)
    window.addEventListener('resize', checkIsDesktop)
})

onBeforeUnmount(() => {
    if (!card.value) return

    card.value.removeEventListener('mousemove', handleMouseMove)
    card.value.removeEventListener('mouseleave', handleMouseLeave)
    window.removeEventListener('resize', checkIsDesktop)
})
</script>
